import * as React from "react";
import { Link } from "react-router-dom";
import { PosterArt, type ArtKind } from "./FieldArt.js";
import { usePrefetchGame } from "../hooks/usePrefetchGame.js";

export interface Poster {
  slug: string;
  title: string;
  desc: string;
  grades: string;
  chip: string;
  art: ArtKind;
  thumbnail: string | null;
}

/**
 * Every game is presented as a K–12 title rather than its specific grade band.
 * Shared so the gallery, the landing grid, and the title ring stay in sync.
 */
export const GRADES_LABEL = "K–12";

export type GalleryVariant = "a" | "b" | "c" | "d" | "e";

interface GalleryProps {
  variant: GalleryVariant;
  posters: Poster[];
  loading: boolean;
}

/**
 * Renders game posters in one of five gallery layouts (A–E).
 * The poster card structure is identical across variants; only the
 * container and scroll/interaction behaviour differs.
 */
export function GamesGallery({ variant, posters, loading }: GalleryProps): React.ReactElement {
  switch (variant) {
    case "a":
      return <HorizontalCarousel posters={posters} loading={loading} />;
    case "b":
      return <VerticalScroll posters={posters} loading={loading} />;
    case "c":
      return <MultiRowHorizontal posters={posters} loading={loading} />;
    case "d":
      return <LimitedGrid posters={posters} loading={loading} />;
    case "e":
      return <Filmstrip posters={posters} loading={loading} />;
  }
}

// ─── shared poster card ──────────────────────────────────────────────────────

function PosterCard({
  p,
  priority = false,
  focusable = true,
}: {
  p: Poster;
  priority?: boolean;
  /** Clone cards in the seamless-loop track are aria-hidden decoration; drop them
      from the tab order so keyboard users don't land on invisible duplicates. */
  focusable?: boolean;
}): React.ReactElement {
  const isPlayable = p.chip !== "Coming soon";
  const prefetch = usePrefetchGame(isPlayable ? p.slug : null);

  return (
    <Link
      className="v-field__poster"
      to={isPlayable ? `/games/${p.slug}` : "/games"}
      {...(focusable ? {} : { tabIndex: -1 })}
      {...prefetch}
    >
      <div className="v-field__poster-art">
        <div className="v-field__poster-chip">{p.chip}</div>
        <PosterArt
          thumbnail={p.thumbnail}
          kind={p.art}
          alt={`${p.title} cover art`}
          priority={priority}
        />
      </div>
      <div className="v-field__poster-body">
        <h3 className="v-field__poster-title">{p.title}</h3>
        <p className="v-field__poster-desc">{p.desc}</p>
        <div className="v-field__poster-meta">
          <span>{GRADES_LABEL}</span>
          <span className="v-field__poster-play">
            Play <PlayIcon />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── variant sub-props ───────────────────────────────────────────────────────

// The gallery lives well below the fold (after the hero, ticker, "why it
// works", and "how it works" sections), so nothing here should eager-load:
// doing so steals bandwidth and fetch priority from the above-the-fold hero
// ring. These cards lazy-load on scroll — and since they share the exact same
// cover URLs as the ring (same `card` srcset/sizes), the bytes are already in
// cache by the time the user reaches them. Kept as a named constant so the
// per-variant `priority={i < PRIORITY_COUNT}` call sites read intentionally.
const PRIORITY_COUNT = 0;

interface VariantProps {
  posters: Poster[];
  loading: boolean;
}

// ─── shared scroll-arrow hook (used by A + C) ────────────────────────────────

function useScrollArrows(ref: React.RefObject<HTMLDivElement | null>) {
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(true);

  const update = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, [ref]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  const scroll = React.useCallback(
    (dir: 1 | -1) => {
      const el = ref.current;
      if (!el) return;
      el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
    },
    [ref],
  );

  return { canLeft, canRight, scroll };
}

// ─── A: Auto-scrolling carousel (filmstrip within container) ─────────────────

function HorizontalCarousel({ posters, loading }: VariantProps): React.ReactElement {
  const duration = Math.max(posters.length * 4, 30);

  return (
    <div className="gallery-a" aria-busy={loading ? "true" : "false"}>
      <div className="gallery-a__track" style={{ animationDuration: `${duration}s` }}>
        {posters.map((p, i) => (
          <PosterCard key={`${p.slug}-${i}`} p={p} priority={i < PRIORITY_COUNT} />
        ))}
        {/* Cloned set for seamless loop — aria-hidden so screen readers skip it,
            and focusable={false} so keyboard tab order skips the duplicates too. */}
        <span aria-hidden="true" style={{ display: "contents" }}>
          {posters.map((p, i) => (
            <PosterCard key={`${p.slug}-clone-${i}`} p={p} focusable={false} />
          ))}
        </span>
      </div>
    </div>
  );
}

// ─── B: Vertical Scroll (capped height, bottom fade) ─────────────────────────

function VerticalScroll({ posters, loading }: VariantProps): React.ReactElement {
  return (
    <div className="gallery-b" aria-busy={loading ? "true" : "false"}>
      <div className="gallery-b__track">
        {posters.map((p, i) => (
          <PosterCard key={p.slug} p={p} priority={i < PRIORITY_COUNT} />
        ))}
      </div>
      <div className="gallery-b__fade" aria-hidden="true" />
    </div>
  );
}

// ─── C: Multi-row Horizontal Scroll (2 rows, scroll-snap) ───────────────────

function MultiRowHorizontal({ posters, loading }: VariantProps): React.ReactElement {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const { canLeft, canRight, scroll } = useScrollArrows(trackRef);

  return (
    <div className="gallery-c" aria-busy={loading ? "true" : "false"}>
      <div className="gallery-c__track" ref={trackRef}>
        {posters.map((p, i) => (
          <PosterCard key={p.slug} p={p} priority={i < PRIORITY_COUNT} />
        ))}
      </div>
      {canLeft && (
        <button
          className="gallery__arrow gallery__arrow--left"
          onClick={() => scroll(-1)}
          aria-label="Scroll left"
        >
          <ChevronIcon direction="left" />
        </button>
      )}
      {canRight && (
        <button
          className="gallery__arrow gallery__arrow--right"
          onClick={() => scroll(1)}
          aria-label="Scroll right"
        >
          <ChevronIcon direction="right" />
        </button>
      )}
    </div>
  );
}

// ─── D: Limited Grid + "View all" button ─────────────────────────────────────

const GRID_LIMIT = 8;

function LimitedGrid({ posters, loading }: VariantProps): React.ReactElement {
  return (
    <div className="gallery-d" aria-busy={loading ? "true" : "false"}>
      <div className="gallery-d__grid">
        {posters.slice(0, GRID_LIMIT).map((p, i) => (
          <PosterCard key={p.slug} p={p} priority={i < PRIORITY_COUNT} />
        ))}
      </div>
      {posters.length > GRID_LIMIT && (
        <div className="gallery-d__more-wrap">
          <Link to="/games" className="gallery-d__more">
            View all {posters.length} games
            <span className="gallery-d__more-arrow">→</span>
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── E: Auto-scrolling Filmstrip (infinite loop, pause on hover) ─────────────

function Filmstrip({ posters, loading }: VariantProps): React.ReactElement {
  return (
    <div className="gallery-e" aria-busy={loading ? "true" : "false"}>
      <div className="gallery-e__track">
        {posters.map((p, i) => (
          <PosterCard key={`${p.slug}-${i}`} p={p} priority={i < PRIORITY_COUNT} />
        ))}
        <span aria-hidden="true" style={{ display: "contents" }}>
          {posters.map((p, i) => (
            <PosterCard key={`${p.slug}-clone-${i}`} p={p} focusable={false} />
          ))}
        </span>
      </div>
    </div>
  );
}

// ─── icons ───────────────────────────────────────────────────────────────────

function PlayIcon(): React.ReactElement {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <polygon points="6 4 20 12 6 20" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: "left" | "right" }): React.ReactElement {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === "left" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 6 15 12 9 18" />
      )}
    </svg>
  );
}
